import { useState } from 'react';
import { Confirm, useNotify, useTranslate } from 'react-admin';
import { Button as MuiButton } from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { PermissionGuard } from 'src/routes/components/permission-guard';

import { resetProxyQuarantine } from '../../services/proxyManagerService';
import { PROXY_MANAGER_PERMISSION_RESOURCE, QUARANTINED_LEVELS } from './constants';

interface ResetQuarantineButtonProps {
  proxyId?: string;
  address: string;
  quarantineLevel?: string | null;
  onSuccess?: () => void;
}

export const ResetQuarantineButton = ({
  proxyId,
  address,
  quarantineLevel,
  onSuccess,
}: ResetQuarantineButtonProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!QUARANTINED_LEVELS.includes(quarantineLevel ?? '')) return null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await resetProxyQuarantine(String(proxyId));
      notify(translate('module.proxyManager.resetQuarantineSuccess'), {
        type: 'success',
        autoHideDuration: 1500,
      });
      onSuccess?.();
    } catch (error: any) {
      notify(error?.body?.message ?? error?.message ?? translate('module.proxyManager.unknownError'), {
        type: 'error',
      });
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <PermissionGuard
      resource={PROXY_MANAGER_PERMISSION_RESOURCE}
      action="update"
      fallback={<></>}
    >
      <MuiButton
        variant="outlined"
        color="warning"
        size="small"
        disabled={!proxyId}
        startIcon={<Iconify icon="solar:shield-cross-bold" />}
        onClick={() => setOpen(true)}
        sx={{ minWidth: 160 }}
      >
        {translate('module.proxyManager.resetQuarantine')}
      </MuiButton>
      <Confirm
        isOpen={open}
        loading={loading}
        title={translate('module.proxyManager.resetQuarantine')}
        content={translate('module.proxyManager.resetQuarantineConfirm', {
          address,
          level: quarantineLevel,
        })}
        onConfirm={handleConfirm}
        onClose={() => setOpen(false)}
      />
    </PermissionGuard>
  );
};

export default ResetQuarantineButton;
